import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

import { CAPTCHA_ENABLED, CAPTCHA_SITE_KEY } from "../../helpers/constants";

// Único punto del front que conoce Cloudflare Turnstile. El resto de la app
// habla con este wrapper mediante `onToken` / `onExpire` / `onError` y el
// `reset()` expuesto por la ref, de modo que cambiar de proveedor de captcha
// no obligue a tocar los formularios que lo usan.
const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";

// Idempotente: si el script ya está en el DOM (o ya se ejecutó) no se vuelve a
// insertar, solo se espera a que termine de cargar.
const loadTurnstileScript = () =>
  new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }

    const existing = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("captcha script error")));
      return;
    }

    const script = document.createElement("script");
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () => reject(new Error("captcha script error")));
    document.head.appendChild(script);
  });

const CaptchaWidget = forwardRef(({ onToken, onExpire, onError }, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    },
  }));

  useEffect(() => {
    if (!CAPTCHA_ENABLED) return undefined;
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: CAPTCHA_SITE_KEY,
          theme: "light",
          callback: (token) => onToken?.(token),
          "expired-callback": () => onExpire?.(),
          "error-callback": () => onError?.(),
        });
      })
      .catch((error) => {
        console.error("captcha debug: error loading turnstile script", error);
        if (!cancelled) onError?.();
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!CAPTCHA_ENABLED) return null;

  return <div className="gm-captcha-container" ref={containerRef} />;
});

export default CaptchaWidget;
